import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { Image } from '$partials/media';
import { linearScale } from '$utilities/scale';

/* =========================================================================  */
/* Component(s) */
/* =========================================================================  */

/**
 * @typedef {PropTypes.InferProps<typeof propTypes>} Props
 */
const AwardsModuleLandingHeader = ({
    images = [],
    logo = null,
    title = ''
}) => {
    // misc: chunk images as per design
    const desktopImageGroup1 = images.slice(0, 2);
    const desktopImageGroup2 = images.slice(2, 5);
    const desktopImageGroup3 = images.slice(5, 8);
    const _desktopImages = [];

    if (desktopImageGroup1.length > 0) {
        _desktopImages.push(desktopImageGroup1);
    }

    if (desktopImageGroup2.length > 0) {
        _desktopImages.push(desktopImageGroup2);
    }

    if (desktopImageGroup3.length > 0) {
        _desktopImages.push(desktopImageGroup3);
    }

    const mobileImageGroup1 = images.slice(0, 2);
    const mobileImageGroup2 = images.slice(2, 3);
    const mobileImageGroup3 = images.slice(3, 4);
    const _mobileImages = [];

    if (mobileImageGroup1.length > 0) {
        _mobileImages.push(mobileImageGroup1);
    }

    if (mobileImageGroup2.length > 0) {
        _mobileImages.push(mobileImageGroup2);
    }

    if (mobileImageGroup3.length > 0) {
        _mobileImages.push(mobileImageGroup3);
    }

    // references
    const desktopImageGroup1Ref = useRef();
    const desktopImageGroup2Ref = useRef();
    const desktopImageGroup3Ref = useRef();
    const desktopLogoRef = useRef();
    const mobileImageGroup1Ref = useRef();
    const mobileImageGroup2Ref = useRef();
    const mobileImageGroup3Ref = useRef();
    const mobileLogoRef = useRef();
    const moduleRef = useRef();

    // state
    const [ screenSize, setScreenSize ] = useState('small');

    // methods
    const handleResize = () => {
        if (window.innerWidth < 1024) {
            if (screenSize !== 'small') {
                setScreenSize('small');
            }
        } else {
            if (screenSize !== 'large') {
                setScreenSize('large');
            }
        }
    };

    const handleScroll = () => {
        // desktop: logo transform
        if (desktopLogoRef.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 20);
            desktopLogoRef.current.style.transform = `translate3d(0, ${transformValue}%, 0)`;
        }

        // desktop: image group 1 transform
        if (desktopImageGroup1Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight * 1.5, 0, 35); // * 1.5 to appear slower in transition
            desktopImageGroup1Ref.current.style.transform = `translate3d(0, ${transformValue}%, 0)`;
        }

        // desktop: image group 2 transform
        if (desktopImageGroup2Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 20);
            desktopImageGroup2Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // desktop: image group 3 transform
        if (desktopImageGroup3Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight * 1.5, 0, 30); // * 1.5 to appear slower in transition
            desktopImageGroup3Ref.current.style.transform = `translate3d(0, ${transformValue}%, 0)`;
        }

        // mobile: logo transform
        if (mobileLogoRef.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 60);
            mobileLogoRef.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // mobile: image group 1 transform
        if (mobileImageGroup1Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 30);
            mobileImageGroup1Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // mobile: image group 2 transform
        if (mobileImageGroup2Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 35);
            mobileImageGroup2Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }

        // mobile: image group 3 transform
        if (mobileImageGroup3Ref.current && moduleRef.current) {
            const transformValue = linearScale(window.scrollY, 0, moduleRef.current.offsetHeight, 0, 25);
            mobileImageGroup3Ref.current.style.transform = `translate3d(0, -${transformValue}%, 0)`;
        }
    };

    // hooks
    useEffect(() => {
        window.addEventListener('resize', handleResize, { passive: true });
        window.addEventListener('scroll', handleScroll, { passive: true });

        handleResize();
        // component unmount
        return () => {
            window.removeEventListener('resize', handleResize, { passive: true });
            window.removeEventListener('scroll', handleScroll, { passive: true });
        };
    }, [ screenSize ]);

    // misc: helper array for rendering later
    const imageDesktopRefArray = [
        desktopImageGroup1Ref,
        desktopImageGroup2Ref,
        desktopImageGroup3Ref
    ];

    const imageMobileRefArray = [
        mobileImageGroup1Ref,
        mobileImageGroup2Ref,
        mobileImageGroup3Ref
    ];

    // render
    return (
        <section ref={moduleRef}>
            {title && (
                <h1 className='sr-only'>
                    {title}
                </h1>
            )}

            {/* mb -80px here to negate following module mt +80px / -mt-[83px] md:-mt-[103px] to negate height of the site header */}
            <div
                aria-hidden={true}
                className='h-[812px] sm:h-auto md:h-[800px] w-full overflow-hidden -mb-80 -mt-[83px] md:-mt-[103px]'>
                {/* mobile */}
                {screenSize === 'small' && (
                    <div className='layout-container'>
                        <div className='px-10 sm:px-20'>
                            {/* image grid 1 */}
                            <div className='flex gap-20 sm:h-[800px] md:h-auto'>
                                {/* left col */}
                                {mobileImageGroup1.length > 0 && (
                                    <ul
                                        className='basis-full mt-150'
                                        ref={imageMobileRefArray[0]}>
                                        {mobileImageGroup1.map((item, idx) => {
                                            return (
                                                <li
                                                    className={`mb-20${idx === 1 ? ' -ml-140 sm:-ml-40 aspect-[336/150]' : ' aspect-square'}`}
                                                    key={idx}>
                                                    <div className='h-full w-full'>
                                                        <Image
                                                            {...item.image}
                                                            className={'h-full w-full object-cover'}
                                                            containerClassName={'h-full w-full'}
                                                            containerStyle={{
                                                                height: '100%',
                                                                paddingBottom: 0
                                                            }}
                                                            role='presentation'
                                                            src={item.image?.src} />
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}

                                {/* right col */}
                                {mobileImageGroup2.length > 0 && (
                                    <ul
                                        className='basis-full mt-100'
                                        ref={imageMobileRefArray[1]}>
                                        {mobileImageGroup2.map((item, idx) => {
                                            return (
                                                <li
                                                    className='mb-20 aspect-[157/256]'
                                                    key={idx}>
                                                    <div className='h-full w-full'>
                                                        <Image
                                                            {...item.image}
                                                            className={'h-full w-full object-cover'}
                                                            containerClassName={'h-full w-full'}
                                                            containerStyle={{
                                                                height: '100%',
                                                                paddingBottom: 0
                                                            }}
                                                            role='presentation'
                                                            src={item.image?.src}
                                                            width={item.image?.width} />
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>

                            {/* logo */}
                            <div className='w-full my-20 relative'>
                                {logo && (
                                    <img
                                        alt={logo.alt}
                                        className='w-[278px] -ml-30 pointer-events-none'
                                        height={logo.height}
                                        loading='eager'
                                        ref={mobileLogoRef}
                                        src={logo.src}
                                        width={logo.width} />
                                )}
                            </div>

                            {/* image grid 3 */}
                            <div className='flex gap-20 mb-20'>
                                <ul className='w-1/4' />

                                {mobileImageGroup3.length > 0 && (
                                    <ul
                                        className='w-3/4'
                                        ref={imageMobileRefArray[2]}>
                                        {mobileImageGroup3.map((item, idx) => {
                                            return (
                                                <li
                                                    className='mb-20 aspect-square'
                                                    key={idx}>
                                                    <div className='h-full w-full'>
                                                        <Image
                                                            {...item.image}
                                                            className={'h-full w-full object-cover'}
                                                            containerClassName={'h-full w-full'}
                                                            containerStyle={{
                                                                height: '100%',
                                                                paddingBottom: 0
                                                            }}
                                                            role='presentation'
                                                            src={item.image?.src} />
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {/* desktop */}
                {screenSize === 'large' && (
                    <div className='h-full layout-container text-white'>
                        <div className='h-full px-10 sm:px-20'>
                            <div className='h-full flex flex-wrap pointer-events-none select-none'>
                                {/* left col */}
                                <div
                                    className='h-full w-full md:w-5/12 flex'
                                    ref={desktopLogoRef}>
                                    {logo && (
                                        <img
                                            className='w-[278px] md:w-[395px] -ml-30 md:-ml-40 my-auto'
                                            height={logo.height}
                                            loading='eager'
                                            role='presentation'
                                            src={logo.src}
                                            width={logo.width} />
                                    )}
                                </div>

                                {/* right col */}
                                <div className='h-full w-full md:w-7/12'>
                                    <div className='w-[1130px] flex gap-40'>
                                        {/* image grid */}
                                        {_desktopImages && _desktopImages.length > 0 && (
                                            _desktopImages.map((chunk, idx) => {
                                                return (
                                                    <ul
                                                        className={`basis-full${idx === 0 ? ' mt-140' : ''}${idx === 2 ? ' -mt-[390px] flex flex-col justify-between' : ''}`}
                                                        key={idx}
                                                        ref={imageDesktopRefArray[idx]}>
                                                        {chunk.map((item, jdx) => {
                                                            return (
                                                                <li
                                                                    className='mb-40'
                                                                    key={`${idx}-${jdx}`}>
                                                                    <div className={`${(((idx + 1) % 2 === 0) && (jdx % 2 === 0)) ? ' w-6/12' : null}${(idx === 2 && jdx === 0) ? ' -ml-[170px] -mt-150' : ''}${(idx === 2 && jdx === 1) ? ' -mt-[350px]' : ''}`}>
                                                                        <Image
                                                                            {...item.image}
                                                                            height={item.image?.height}
                                                                            role='presentation'
                                                                            src={item.image?.src}
                                                                            width={item.image?.width} />
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                );
                                            }))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

/* =========================================================================  */
/* Typing and export(s) */
/* =========================================================================  */

const propTypes = {
    images: PropTypes.array,
    logo: PropTypes.object.isRequired,
    title: PropTypes.string.isRequired
};

AwardsModuleLandingHeader.propTypes = propTypes;

export default AwardsModuleLandingHeader;
